import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

// Scroll to top on every route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
import { createPageUrl } from '@/utils';
import { ROUTE_ROBOTS_PAGE_NAMES } from '@/components/RouteRobots';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Stethoscope, 
  User, 
  LogOut, 
  Building2, 
  Briefcase,
  Menu,
  X,
  ChevronDown,
  Users,
  ArrowLeft,
  Settings,
  Activity,
  FileSpreadsheet
} from 'lucide-react';
import MobileBottomNav from '../components/MobileBottomNav';
import ThemeToggle from '../components/ThemeToggle';
import PageTransition from '../components/PageTransition';
import Footer from '../components/Footer';

// Pages an authenticated platform administrator may open without being sent
// through role onboarding. Deliberately NOT the public-page allowlist below:
// this exemption requires User.role === 'admin', and every page named here
// still sits behind ProtectedRoute and still enforces its own admin check.
const ADMIN_ONBOARDING_EXEMPT_PAGES = ['JobFeedReconciliation'];

const isAdminExemptPage = (user, currentPageName) =>
  user?.role === 'admin' && ADMIN_ONBOARDING_EXEMPT_PAGES.includes(currentPageName);

export default function Layout({ children, currentPageName }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user) {
      const publicPages = ['Home', 'Blog', 'BlogPost', 'Forum', 'ForumPost', 'ForEmployers', 'PostJob', 'JobDetails', 'JobsByLocation', 'JobSpecialties', 'NursingJobs', 'SalaryGuide', 'SalaryCalculator', 'ROICalculator', 'SuccessStories', 'SpecialtyJobsByLocation', 'LocalJobs', 'Newsletter', 'EmployerDirectory', 'ContactSupport', 'ResumeBuilder', 'Subscription', 'IndexingDashboard', 'JobsRoute', 'HealthcareJobsHub', 'JobsHub', 'HealthcareJobsRoot'];
      if (!user.user_type && !isAdminExemptPage(user, currentPageName) && currentPageName !== 'Onboarding' && !publicPages.includes(currentPageName)) {
        navigate(createPageUrl('Onboarding'));
      }
    }
  }, [user, isAuthenticated, currentPageName, navigate]);

  const handleLogout = () => {
    base44.auth.logout("/");
  };

  const getDashboardUrl = () => {
    if (!user?.user_type) return createPageUrl('Onboarding');
    if (user.user_type === 'facility') {
      return createPageUrl('FacilityDashboard');
    }
    if (user.user_type === 'staffing_agency') {
      return createPageUrl('StaffingAgencyDashboard');
    }
    return createPageUrl('WorkerDashboard');
  };

  // Don't show header on onboarding, setup pages, or landing
  const hideNav = ['Onboarding', 'FacilitySetup', 'StaffingAgencySetup', 'WorkerProfile'].includes(currentPageName);

  const isRootPage = currentPageName === 'Home' || currentPageName === 'FacilityDashboard' || currentPageName === 'StaffingAgencyDashboard' || currentPageName === 'WorkerDashboard';
  const canGoBack = !isRootPage && window.history.length > 1;

  // Pages that should be indexed by search engines
  // NOTE: 'JobsRoute' and 'HealthcareJobsHub' are listed so this shared layout
  // does not emit its own noindex tag — per-page indexability on those routes is
  // decided solely by evaluateSeoPageQuality() and rendered by the page itself.
  const indexablePages = ['Home', 'Blog', 'BlogPost', 'Forum', 'ForumPost', 'ForEmployers', 'PostJob', 'JobDetails', 'JobsByLocation', 'JobSpecialties', 'NursingJobs', 'SalaryGuide', 'SalaryCalculator', 'ROICalculator', 'SuccessStories', 'SpecialtyJobsByLocation', 'SpecialtyJobs', 'LocalJobs', 'Newsletter', 'EmployerDirectory', 'ContactSupport', 'ResumeBuilder', 'Subscription', 'Careers', 'IndustryGuides', 'GuideDetail', 'HealthcareStats', 'Partnerships', 'PrivacyPolicy', 'Terms', 'StateSalaryGuide', 'EmployerResources', 'JobsRoute', 'HealthcareJobsHub', 'JobsHub', 'HealthcareJobsRoot'];
  const isIndexable = indexablePages.includes(currentPageName);

  if (hideNav) {
    return (
      <HelmetProvider>
        <div className="bg-slate-50 dark:bg-slate-950">
          <ScrollToTop />
          {children}
        </div>
      </HelmetProvider>
    );
  }

  return (
    <HelmetProvider>
      <Helmet>
        <html lang="en" />
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" />
        {/* Two private routes are owned by RouteRobots, which manages their
            directive for the whole session and removes it again on the way out.
            Skipping them here keeps exactly one robots tag on those routes
            instead of two identical ones. Every other page is unchanged. */}
        {!isIndexable && !ROUTE_ROBOTS_PAGE_NAMES.includes(currentPageName) && <meta name="robots" content="noindex, nofollow" />}
        <link rel="icon" type="image/png" href="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/render/image/public/base44-prod/public/696ac43216f2dcd8c8bf25f9/af21d9783_Clinivologo.PNG?width=128&height=128&resize=contain&quality=80" />
        <link rel="apple-touch-icon" href="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/render/image/public/base44-prod/public/696ac43216f2dcd8c8bf25f9/af21d9783_Clinivologo.PNG?width=128&height=128&resize=contain&quality=80" />
        <meta name="theme-color" content="#0d9488" />
      </Helmet>
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <ScrollToTop />
      {/* Navigation */}
      <nav className="bg-white dark:bg-slate-900 border-b dark:border-slate-800 sticky top-0 z-50" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo / Back Button */}
            <div className="flex items-center gap-3">
              {canGoBack && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(-1)}
                  className="md:hidden select-none"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              )}
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/render/image/public/base44-prod/public/696ac43216f2dcd8c8bf25f9/af21d9783_Clinivologo.PNG?width=224&height=224&resize=contain&quality=80"
                  alt="Clinivo"
                  width={64}
                  height={64}
                  className="h-16 w-16 object-contain"
                />
              </Link>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              <Link 
                to={"/"} 
                className="text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors font-medium select-none"
              >
                Find Jobs
              </Link>
              {(!user || user.user_type !== 'worker') && (
                <>
                  <Link 
                    to={createPageUrl('ForEmployers')} 
                    className="text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors font-medium select-none"
                  >
                    For Employers
                  </Link>
                  <Link 
                    to={createPageUrl('PostJob')} 
                    className="text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors font-medium select-none"
                  >
                    Post Jobs
                  </Link>
                </>
              )}
              <Link 
                to={createPageUrl('Blog')} 
                className="text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors font-medium select-none"
              >
                Blog
              </Link>
              <Link 
                to={createPageUrl('Forum')} 
                className="text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors font-medium select-none"
              >
                Forum
              </Link>
              <Link 
                to={createPageUrl('Careers')} 
                className="text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors font-medium select-none"
              >
                Careers
              </Link>
              <Link 
                to={createPageUrl('IndustryGuides')} 
                className="text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors font-medium select-none"
              >
                Guides
              </Link>
              {isAuthenticated && user?.user_type === 'facility' && (
                <>
                  <Link 
                    to={createPageUrl('FacilityDashboard')} 
                    className="text-slate-600 hover:text-teal-600 transition-colors font-medium"
                  >
                    Dashboard
                  </Link>
                  <Link 
                    to={createPageUrl('Assessments')} 
                    className="text-slate-600 hover:text-teal-600 transition-colors font-medium"
                  >
                    Assessments
                  </Link>
                  <Link 
                    to={createPageUrl('FacilityDocuments')} 
                    className="text-slate-600 hover:text-teal-600 transition-colors font-medium"
                  >
                    Documents
                  </Link>
                  <Link 
                    to={createPageUrl('Subscription')} 
                    className="text-slate-600 hover:text-teal-600 transition-colors font-medium"
                  >
                    Pricing
                  </Link>
                  </>
                  )}
                  {isAuthenticated && user?.user_type === 'staffing_agency' && (
                <>
                  <Link 
                    to={createPageUrl('StaffingAgencyDashboard')} 
                    className="text-slate-600 hover:text-teal-600 transition-colors font-medium"
                  >
                    Dashboard
                  </Link>
                  <Link 
                    to={createPageUrl('Assessments')} 
                    className="text-slate-600 hover:text-teal-600 transition-colors font-medium"
                  >
                    Assessments
                  </Link>
                  <Link 
                    to={createPageUrl('FacilityDocuments')} 
                    className="text-slate-600 hover:text-teal-600 transition-colors font-medium"
                  >
                    Documents
                  </Link>
                  <Link 
                    to={createPageUrl('Subscription')} 
                    className="text-slate-600 hover:text-teal-600 transition-colors font-medium"
                  >
                    Pricing
                  </Link>
                  </>
                  )}
                  {isAuthenticated && user?.user_type === 'worker' && (
                <>
                  <Link 
                    to={createPageUrl('WorkerDashboard')} 
                    className="text-slate-600 hover:text-teal-600 transition-colors font-medium"
                  >
                    My Applications
                  </Link>
                  <Link 
                    to={createPageUrl('Network')} 
                    className="text-slate-600 hover:text-teal-600 transition-colors font-medium"
                  >
                    Network
                  </Link>
                  <Link 
                    to={createPageUrl('Messages')} 
                    className="text-slate-600 hover:text-teal-600 transition-colors font-medium"
                  >
                    Messages
                  </Link>
                  <Link 
                    to={createPageUrl('CandidateOnboarding')} 
                    className="text-slate-600 hover:text-teal-600 transition-colors font-medium"
                  >
                    Onboarding
                  </Link>
                  <Link 
                    to={createPageUrl('Referrals')} 
                    className="text-slate-600 hover:text-teal-600 transition-colors font-medium"
                  >
                    Referrals
                  </Link>
                  <Link 
                    to={createPageUrl('ManageAlerts')} 
                    className="text-slate-600 hover:text-teal-600 transition-colors font-medium"
                  >
                    Manage Alerts
                  </Link>
                  </>
                  )}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2">
              <ThemeToggle />
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="gap-2 select-none">
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                        <User className="h-4 w-4 text-white" />
                      </div>
                      <span className="hidden sm:block font-medium dark:text-white">{user?.full_name?.split(' ')[0]}</span>
                      <ChevronDown className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 dark:bg-slate-900 dark:border-slate-800">
                    <DropdownMenuItem onClick={() => navigate(getDashboardUrl())} className="select-none dark:text-slate-200 dark:focus:bg-slate-800">
                      {user?.user_type === 'facility' || user?.user_type === 'staffing_agency' ? (
                        <>
                          <Building2 className="h-4 w-4 mr-2" />
                          Dashboard
                        </>
                      ) : user?.user_type === 'worker' ? (
                        <>
                          <Briefcase className="h-4 w-4 mr-2" />
                          My Applications
                        </>
                      ) : (
                        <>
                          <User className="h-4 w-4 mr-2" />
                          Dashboard
                        </>
                      )}
                    </DropdownMenuItem>
                    {(user?.user_type === 'facility' || user?.user_type === 'staffing_agency') && user?.facility_role === 'admin' && (
                      <DropdownMenuItem onClick={() => navigate(createPageUrl('TeamManagement'))} className="select-none dark:text-slate-200 dark:focus:bg-slate-800">
                        <Users className="h-4 w-4 mr-2" />
                        Team Management
                      </DropdownMenuItem>
                    )}
                    {user?.role === 'admin' && (
                      <>
                        <DropdownMenuItem onClick={() => navigate(createPageUrl('IndexingDashboard'))} className="select-none dark:text-slate-200 dark:focus:bg-slate-800">
                          <Activity className="h-4 w-4 mr-2" />
                          Indexing Monitor
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(createPageUrl('JobFeedReconciliation'))} className="select-none dark:text-slate-200 dark:focus:bg-slate-800">
                          <FileSpreadsheet className="h-4 w-4 mr-2" />
                          Job Feed Reconciliation
                        </DropdownMenuItem>
                      </>
                    )}
                    {user?.user_type === 'facility' && (
                      <>
                        <DropdownMenuItem onClick={() => navigate(createPageUrl('FacilityProfile'))} className="select-none dark:text-slate-200 dark:focus:bg-slate-800">
                          <Building2 className="h-4 w-4 mr-2" />
                          Facility Profile
                        </DropdownMenuItem>
                      </>
                    )}
                    {user?.user_type === 'staffing_agency' && (
                      <>
                        <DropdownMenuItem onClick={() => navigate(createPageUrl('StaffingAgencyProfile'))} className="select-none dark:text-slate-200 dark:focus:bg-slate-800">
                          <Building2 className="h-4 w-4 mr-2" />
                          Agency Profile
                        </DropdownMenuItem>
                      </>
                    )}
                    {user?.user_type === 'worker' && (
                      <DropdownMenuItem onClick={() => navigate(createPageUrl('WorkerDashboard'))} className="select-none dark:text-slate-200 dark:focus:bg-slate-800">
                        <User className="h-4 w-4 mr-2" />
                        Dashboard
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate(createPageUrl('AccountSettings'))} className="select-none dark:text-slate-200 dark:focus:bg-slate-800">
                      <Settings className="h-4 w-4 mr-2" />
                      Account Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="dark:bg-slate-800" />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600 dark:text-red-400 select-none dark:focus:bg-slate-800">
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => base44.auth.redirectToLogin(window.location.href)}
                    className="hidden sm:inline-flex select-none dark:text-slate-200"
                  >
                    Sign In
                  </Button>
                  <Button 
                    onClick={() => base44.auth.redirectToLogin(window.location.href)}
                    className="bg-teal-600 hover:bg-teal-700 select-none"
                  >
                    Get Started
                  </Button>
                </>
              )}

              {/* Mobile menu button */}
              <Button 
                variant="ghost" 
                size="icon"
                className="md:hidden select-none"
                aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-navigation"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div id="mobile-navigation" className="md:hidden border-t dark:border-slate-800 bg-white dark:bg-slate-900">
            <div className="px-4 py-4 space-y-3">
              <Link 
                to={"/"} 
                className="block py-2 text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 font-medium select-none"
                onClick={() => setMobileMenuOpen(false)}
              >
                Find Jobs
              </Link>
              {(!user || user.user_type !== 'worker') && (
                <>
                  <Link 
                    to={createPageUrl('ForEmployers')} 
                    className="block py-2 text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 font-medium select-none"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    For Employers
                  </Link>
                  <Link 
                    to={createPageUrl('PostJob')} 
                    className="block py-2 text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 font-medium select-none"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Post Jobs
                  </Link>
                </>
              )}
              <Link 
                to={createPageUrl('Blog')} 
                className="block py-2 text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 font-medium select-none"
                onClick={() => setMobileMenuOpen(false)}
              >
                Blog
              </Link>
              <Link 
                to={createPageUrl('Forum')} 
                className="block py-2 text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 font-medium select-none"
                onClick={() => setMobileMenuOpen(false)}
              >
                Forum
              </Link>
              {isAuthenticated && user?.user_type === 'facility' && (
                <Link 
                  to={createPageUrl('FacilityDashboard')} 
                  className="block py-2 text-slate-600 hover:text-teal-600 font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
              )}
              {isAuthenticated && user?.user_type === 'staffing_agency' && (
                <Link 
                  to={createPageUrl('StaffingAgencyDashboard')} 
                  className="block py-2 text-slate-600 hover:text-teal-600 font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
              )}
              {isAuthenticated && user?.user_type === 'worker' && (
                <>
                  <Link 
                    to={createPageUrl('WorkerDashboard')} 
                    className="block py-2 text-slate-600 hover:text-teal-600 font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    My Applications
                  </Link>
                  <Link 
                    to={createPageUrl('Network')} 
                    className="block py-2 text-slate-600 hover:text-teal-600 font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Network
                  </Link>
                  <Link 
                    to={createPageUrl('CandidateInbox')} 
                    className="block py-2 text-slate-600 hover:text-teal-600 font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Messages
                  </Link>
                  <Link 
                    to={createPageUrl('CandidateOnboarding')} 
                    className="block py-2 text-slate-600 hover:text-teal-600 font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Onboarding
                  </Link>
                  <Link 
                    to={createPageUrl('Referrals')} 
                    className="block py-2 text-slate-600 hover:text-teal-600 font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Referrals
                  </Link>
                  <Link 
                    to={createPageUrl('ManageAlerts')} 
                    className="block py-2 text-slate-600 hover:text-teal-600 font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Manage Alerts
                  </Link>
                  </>
                  )}
              {!isAuthenticated && (
                <Button 
                  onClick={() => base44.auth.redirectToLogin(window.location.href)}
                  className="w-full mt-2 select-none"
                  variant="outline"
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Page Content */}
      <main className="pb-20 md:pb-0">
        <PageTransition>
          {children}
        </PageTransition>
      </main>

      {/* Mobile Bottom Navigation */}
      {isAuthenticated && <MobileBottomNav user={user} />}

      {/* Footer - hidden on dashboard pages */}
      {!['FacilityDashboard', 'StaffingAgencyDashboard', 'WorkerDashboard', 'Onboarding', 'FacilitySetup', 'StaffingAgencySetup', 'WorkerProfile'].includes(currentPageName) && <Footer />}
      </div>
      </HelmetProvider>
    );
      }